// src/components/ListeForce.js
import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Typography, Button, Stack } from "@mui/material";
import { useSettings } from "../context/SettingsContext";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from "react-router-dom";



const SKI_VALAIS_LOGO = process.env.PUBLIC_URL + "/logo/img.png";

function ListeForce() {
    const { settings } = useSettings();
    const {
        calcMode,
        calcModeType,
        category,
        selectedCodex,
        selectedDisciplines,
        calcPerDiscipline,   // 🔥 CORRECT
    } = settings;

    const [rows, setRows] = useState([]);
    const [courses, setCourses] = useState([]);
    const [printMode, setPrintMode] = useState(false);

    const now = new Date();
    const timestamp =
        now.toLocaleDateString("fr-CH") +
        " " +
        now.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" });

    useEffect(() => {
        fetch("/listeforce")
            .then((res) => res.json())
            .then((data) => setRows(data));

        fetch("/courses")
            .then((res) => res.json())
            .then((data) => setCourses(data));
    }, []);

    //
    const navigate = useNavigate();

    // ---------------------------------------------------------
    // 1) BEST X PAR DISCIPLINE (strict)
    // ---------------------------------------------------------
    const getBestResultsByDiscipline = (results, discipline) => {
        if (!Array.isArray(results) || selectedCodex.length === 0) return [];
        if (!courses || courses.length === 0) return [];

        const selectedCodexStr = selectedCodex.map(String);

        const filtered = results
            .filter((r) => r.discipline === discipline)   // STRICT
            .map((r) => {
                const p = r.penaltyFinal ?? r.penalty;
                return p == null || p === ""
                    ? null
                    : { ...r, penaltyFinal: parseFloat(p) };
            })
            .filter(Boolean)
            .filter((r) => !Number.isNaN(r.penaltyFinal) && r.penaltyFinal > 0)
            .filter((r) =>
                courses.some(
                    (c) =>
                        selectedCodexStr.includes(String(c.codex)) &&
                        r.discipline === c.discipline &&
                        (r.event === c.event || r.event.includes(c.event))
                )
            );

        filtered.sort((a, b) => a.penaltyFinal - b.penaltyFinal);
        return filtered;
    };

    // ---------------------------------------------------------
    // 2) SCORE FINAL (GLOBAL + PAR DISCIPLINE) — STRICT
    // ---------------------------------------------------------
    const getScore = (results) => {
        if (!Array.isArray(results) || results.length === 0) {
            return { score: null, used: [] };
        }

        const selectedCodexStr = selectedCodex.map(String);

        // === MODE GLOBAL ===
        if (calcModeType === "global") {
            const all = results
                .filter((r) =>
                    selectedDisciplines.length === 0
                        ? true
                        : selectedDisciplines.includes(r.discipline)
                )
                .map((r) => {
                    const p = r.penaltyFinal ?? r.penalty;
                    return p == null || p === ""
                        ? null
                        : { ...r, penaltyFinal: parseFloat(p) };
                })
                .filter(Boolean)
                .filter((r) => !Number.isNaN(r.penaltyFinal) && r.penaltyFinal > 0)
                .filter((r) =>
                    courses.some(
                        (c) =>
                            selectedCodexStr.includes(String(c.codex)) &&
                            r.discipline === c.discipline &&
                            (r.event === c.event || r.event.includes(c.event))
                    )
                );

            if (all.length === 0) return { score: null, used: [] };

            all.sort((a, b) => a.penaltyFinal - b.penaltyFinal);

            const n =
                calcMode === "best1" ? 1 :
                    calcMode === "best2" ? 2 :
                        calcMode === "best3" ? 3 : 1;

            if (all.length < n) return { score: null, used: [] };

            const used = all.slice(0, n);
            const total = used.reduce((acc, r) => acc + r.penaltyFinal, 0);
            const avg = total / used.length;

            return { score: avg, used };
        }

        // === MODE PAR DISCIPLINE ===
        let usedResults = [];

        for (const d of selectedDisciplines) {
            const best = results
                .filter((r) => r.discipline === d)   // STRICT
                .map((r) => {
                    const p = r.penaltyFinal ?? r.penalty;
                    return p == null || p === ""
                        ? null
                        : { ...r, penaltyFinal: parseFloat(p) };
                })
                .filter(Boolean)
                .filter((r) => !Number.isNaN(r.penaltyFinal) && r.penaltyFinal > 0)
                .filter((r) =>
                    courses.some(
                        (c) =>
                            selectedCodexStr.includes(String(c.codex)) &&
                            r.discipline === c.discipline &&
                            (r.event === c.event || r.event.includes(c.event))
                    )
                );

            best.sort((a, b) => a.penaltyFinal - b.penaltyFinal);

            const mode = calcPerDiscipline?.[d] || "best1";   // 🔥 CORRECT
            const required =
                mode === "best1" ? 1 :
                    mode === "best2" ? 2 :
                        mode === "best3" ? 3 : 1;

            if (best.length < required) {
                return { score: null, used: [] };
            }

            const selected = best.slice(0, required);
            usedResults.push(...selected);
        }

        if (usedResults.length === 0) return { score: null, used: [] };

        const total = usedResults.reduce((acc, r) => acc + r.penaltyFinal, 0);
        const avg = total / usedResults.length;

        return { score: avg, used: usedResults };
    };
    // ---------------------------------------------------------
    // 3) COURSES UTILISÉES
    // ---------------------------------------------------------
    const getCourseUsage = (ath) => {
        const { used } = getScore(ath.results || []);

        return courses
            .filter((c) => selectedCodex.includes(String(c.codex)))
            .map((c) => {
                const match = ath.results?.find(
                    (r) => r.event === c.event && r.discipline === c.discipline
                );

                const penalty = match?.penaltyFinal ?? null;
                const num = penalty ? parseFloat(penalty) : null;

                const isUsed = used.some(
                    (r) => r.event === c.event && r.discipline === c.discipline
                );

                return {
                    codex: c.codex,
                    event: c.event,
                    discipline: c.discipline,
                    penalty: num,
                    isUsed,
                };
            });
    };

    // ---------------------------------------------------------
    // 4) COLONNES
    // ---------------------------------------------------------
    const baseColumns = [
        { field: "rank", headerName: "Rang", width: 80, align: "center" },
        { field: "noP", headerName: "No-P.", width: 100, align: "center" },
        { field: "name", headerName: "Nom", width: 200 },
        { field: "year", headerName: "Année", width: 100, align: "center" },
        { field: "category", headerName: "Catégorie", width: 120, align: "center" },
        { field: "club", headerName: "Club", width: 200 },
        { field: "points", headerName: "Points", width: 100, align: "center" },
    ];

    const courseColumns =
        selectedCodex.length === 0
            ? []
            : courses
                .filter((course) => selectedCodex.includes(String(course.codex)))
                .map((course) => ({
                    field: `penalty_${course.codex}`,
                    headerName: `${course.codex} – ${course.event} (${course.discipline})`,
                    width: 220,
                    align: "center",
                    renderCell: (params) => {
                        const res = params.row?.results;
                        if (!Array.isArray(res)) return "—";

                        const match = res.find(
                            (r) =>
                                r.event === course.event &&
                                r.discipline === course.discipline
                        );

                        const val = match?.penaltyFinal ?? null;
                        const num = parseFloat(val);
                        if (!val || Number.isNaN(num) || num <= 0) return "—";

                        const { used } = getScore(res);
                        const isUsed = used.some(
                            (r) =>
                                r.event === course.event &&
                                r.discipline === course.discipline
                        );

                        return (
                            <Typography sx={{ fontWeight: isUsed ? "bold" : "normal" }}>
                                {num.toFixed(2)}
                            </Typography>
                        );
                    },
                }));

    const calcColumn = {
        field: "calcResult",
        headerName:
            calcModeType === "global"
                ? `Résultat (${calcMode})`
                : "Résultat (par discipline)",
        width: 180,
        align: "center",
        renderCell: (params) => {
            const { score } = getScore(params.row?.results);
            return score ? score.toFixed(2) : "—";
        },
    };

    const nbCoursesColumn = {
        field: "nbCourses",
        headerName: "Nb courses",
        width: 140,
        align: "center",
        renderCell: (params) => {
            const { used } = getScore(params.row?.results);
            return used.length;
        },
    };

    const columns = [
        ...baseColumns,
        ...courseColumns,
        calcColumn,
        nbCoursesColumn,
    ];

    // ---------------------------------------------------------
    // 5) FILTRAGE + CLASSEMENT
    // ---------------------------------------------------------
    const filteredRows =
        category && category !== "all"
            ? rows.filter((row) => row.category === category)
            : rows;

    const eligible = [];
    const notEligible = [];

    filteredRows.forEach((ath) => {
        const res = ath.results || [];

        const { score, used } = getScore(res);

        const hasEnoughPerDiscipline = () => {
            if (calcModeType === "global") return score !== null;

            for (const d of selectedDisciplines) {
                const best = getBestResultsByDiscipline(res, d);

                const mode =
                    calcModeType === "global"
                        ? calcMode
                        : calcPerDiscipline?.[d] || "best1";

                const required =
                    mode === "best1" ? 1 :
                        mode === "best2" ? 2 :
                            mode === "best3" ? 3 : 1;

                if (best.length < required) return false;
            }
            return true;
        };

        if (hasEnoughPerDiscipline() && score !== null) {
            eligible.push({
                ...ath,
                score,
                used,
            });
        } else {
            notEligible.push({
                ...ath,
                score: null,
                used: [],
            });
        }
    });

    eligible.sort((a, b) => a.score - b.score);
    eligible.forEach((ath, index) => (ath.rank = index + 1));
    notEligible.forEach((ath) => (ath.rank = null));

    const rankedRows = [...eligible, ...notEligible];

    // ---------------------------------------------------------
    // 6) EXPORT EXCEL
    // ---------------------------------------------------------
    // ---------------------------------------------------------
// 6) EXPORT EXCEL — VERSION OPTIMISÉE
// ---------------------------------------------------------
    const exportExcel = () => {
        const codexOrder = selectedCodex.map(String); // ordre imposé

        const data = rankedRows.map((row) => {
            const usageMap = {};
            getCourseUsage(row).forEach((c) => {
                usageMap[c.codex] = {
                    penalty: c.penalty ? parseFloat(c.penalty).toFixed(2) : "",
                    used: c.isUsed ? "✓" : "",
                };
            });

            // 🔁 1. Colonnes fixes avec Année + Club après Nom
            const entry = {
                Rang: row.rank || "",
                NoP: row.noP,
                Nom: row.name,
                Année: row.year,
                Club: row.club,
                Résultat: row.score ? row.score.toFixed(2) : "",
                Nb_Courses: row.used?.length || 0,
                Généré_le: timestamp,
            };

            // 🔁 2. Colonnes codex = points
            codexOrder.forEach((codex) => {
                entry[codex] = usageMap[codex]?.penalty || "";
            });

            // 🔁 3. Colonnes codex ✓ = utilisé ou non
            codexOrder.forEach((codex) => {
                entry[`${codex} ✓`] = usageMap[codex]?.used || "";
            });

            return entry;
        });

        // 🔁 4. Ordre des colonnes imposé
        const headerOrder = [
            "Rang",
            "NoP",
            "Nom",
            "Année",
            "Club",
            "Résultat",
            "Nb_Courses",
            "Généré_le",
            ...codexOrder,
            ...codexOrder.map((c) => `${c} ✓`),
        ];

        const worksheet = XLSX.utils.json_to_sheet(data, { header: headerOrder });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Liste de Force");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });
        saveAs(new Blob([excelBuffer]), "liste_de_force.xlsx");
    };

    // ---------------------------------------------------------
    // 7) EXPORT PDF
    // ---------------------------------------------------------
    const exportPDF = () => {
        const doc = new jsPDF("landscape", "mm", "a4");

        try {
            doc.addImage(SKI_VALAIS_LOGO, "PNG", 10, 8, 20, 20);
        } catch (e) {}

        doc.setFontSize(16);
        doc.text("Liste de Force – Ski Valais", 40, 20);

        const subtitle = `Mode: ${
            calcModeType === "global" ? calcMode.toUpperCase() : "Par discipline"
        } | Disciplines: ${selectedDisciplines.join(", ")} | Catégorie: ${
            category !== "all" ? category : "Toutes"
        }`;

        doc.setFontSize(10);
        doc.text(subtitle, 40, 26);
        doc.text(`Généré le : ${timestamp}`, 40, 32);

        const tableData = rankedRows.map((row) => {
            const courseUsage = getCourseUsage(row);

            const courseText = courseUsage
                .map((c) => {
                    const val = c.penalty ? c.penalty.toFixed(2) : "—";
                    return `• ${c.codex} (${c.discipline}) : ${val}${
                        c.isUsed ? " *" : ""
                    }`;
                })
                .join("\n");

            return [
                row.rank || "",
                row.noP,
                row.name,
                row.year,
                row.category,
                row.club,
                row.points || "",
                row.score ? row.score.toFixed(2) : "",
                row.used?.length || "",
                courseText,
            ];
        });

        autoTable(doc, {
            startY: 38,
            head: [
                [
                    "Rang",
                    "NoP",
                    "Nom",
                    "Année",
                    "Cat.",
                    "Club",
                    "Points",
                    "Résultat",
                    "Nb",
                    "Courses",
                ],
            ],
            body: tableData,
            styles: { fontSize: 8, halign: "center" },
            headStyles: { fillColor: [192, 0, 0], halign: "center" },
            columnStyles: {
                2: { halign: "left" },
                5: { halign: "left" },
                9: { halign: "left" },
            },
        });

        doc.save("liste_de_force.pdf");
    };
    // ---------------------------------------------------------
    // 8) IMPRESSION
    // ---------------------------------------------------------
    const handlePrint = () => {
        setPrintMode(true);
        setTimeout(() => {
            window.print();
            setPrintMode(false);
        }, 300);
    };

    // ---------------------------------------------------------
    // 9) RENDER
    // ---------------------------------------------------------
    return (
        <Box sx={{ width: "100%", padding: 2 }}>
            <Box
                className={printMode ? "print-mode" : ""}
                sx={{
                    backgroundColor: printMode ? "white" : "inherit",

                    ...(printMode && {
                        transform: "rotate(90deg)",
                        transformOrigin: "top left",
                        width: "100vh",
                        height: "100vw",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        padding: 2,
                        zIndex: 9999,
                    }),
                }}
            >
                {/* === HEADER SKI-VALAIS === */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <img
                            src={SKI_VALAIS_LOGO}
                            alt="Ski Valais"
                            style={{ width: 60, height: "auto" }}
                        />
                        <Box>
                            <Typography variant="h5">
                                Liste de Force – Ski Valais
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Mode:{" "}
                                {calcModeType === "global"
                                    ? calcMode.toUpperCase()
                                    : "Par discipline"}{" "}
                                | Disciplines: {selectedDisciplines.join(", ")}{" "}
                                {category && category !== "all"
                                    ? `| Catégorie: ${category}`
                                    : ""}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Généré le : {timestamp}
                            </Typography>
                        </Box>
                    </Box>

                    {!printMode && (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={exportExcel}>
                                Export Excel
                            </Button>
                            <Button variant="outlined" onClick={exportPDF}>
                                Export PDF
                            </Button>
                        </Stack>
                    )}
                </Box>

                <DataGrid
                    rows={rankedRows}
                    columns={columns}
                    pageSize={printMode ? rankedRows.length : 20}
                    rowsPerPageOptions={[20, 50, 100]}
                    getRowId={(row) => row.noP}
                    hideFooter={printMode}
                    autoHeight={printMode}
                    sx={{ cursor: "pointer" }}
                    onRowClick={(params) => navigate(`/athlete/${params.row.noP}`)}
                />

            </Box>
        </Box>
    );
}

export default ListeForce;
